<template>
  <tbody>
    <tr
      v-for="(receivedAmount, raIndex) in billReceivedAmounts"
      :key="`${bill.id}-${receivedAmount.id || 'new-' + raIndex}`"
      :class="getRowClass()"
      @click="handleRowClick"
    >
      <!-- Show bill details only in first row -->
      <template v-if="raIndex === 0">
        <td
          :rowspan="billReceivedAmounts.length"
          @click.stop
        >
          <b-form-checkbox
            :checked="isSelected"
            @change="handleSelectionChange"
          />
        </td>
        <td
          :rowspan="billReceivedAmounts.length"
          style="font-size: 0.75rem;"
        >
          <div style="display: flex; gap: 2px;">
            <span style="color: #4299e1; font-weight: 600;">{{ bill.bill_no }}</span>
            <span>{{ formatedDate(bill.date, 'DD-MM-YYYY') }}</span>
          </div>
          <div class="mt-25 d-flex align-items-center">
            <span
              v-for="dotIndex in bill.statusCount"
              :key="dotIndex"
              v-b-tooltip.hover
              class="status-dot mr-25"
              :class="getStatusDotClass(dotIndex)"
              :title="getStatusTooltip(dotIndex)"
            />
          </div>
        </td>
        <td
          :rowspan="billReceivedAmounts.length"
          style="font-size: 0.75rem;"
        >
          {{ getPartyName(bill.party__identifier) }}
        </td>
        <td :rowspan="billReceivedAmounts.length">
          {{ getPartyArea(bill.party__identifier) }}
        </td>
        <td :rowspan="billReceivedAmounts.length">
          <b-badge
            v-if="bill.calc_payment_status"
            :variant="getPayStatusVariant(bill.calc_payment_status)"
            class="text-capitalize"
          >
            {{ bill.calc_payment_status.replace('_', ' ') }}
          </b-badge>
          <span
            v-else
            class="text-muted"
          >-</span>
        </td>
        <td
          :rowspan="billReceivedAmounts.length"
          class="text-right"
        >
          {{ formatAmount(bill.amount) }}
        </td>
        <td
          :rowspan="billReceivedAmounts.length"
          class="text-right"
        >
          <span :class="getDueAmountClass()">
            {{ formatAmount(calculateTotalDueAmount()) }}
          </span>
        </td>
      </template>

      <!-- Received Amount - Always show for each row -->
      <td class="text-right">
        <b-form-input
          v-if="receivedAmount.isEditing"
          v-model.number="receivedAmount.editData.received_amount"
          type="number"
          size="sm"
          class="text-right"
          :disabled="receivedAmount.isUpdating"
          :class="{ 'border-danger': receivedAmount.editData.received_amount !== null && receivedAmount.editData.received_amount <= 0 }"
          @click="handleReceivedAmountFocus(receivedAmount, $event)"
        />
        <span v-else>
          <template v-if="receivedAmount.received_amount">
            {{ formatAmount(receivedAmount.received_amount) }}
          </template>
          <span
            v-else-if="calculateTotalDueAmount() > 0"
            class="text-primary"
            style="font-style: italic; font-size: 0.85rem; opacity: 0.6;"
          >
            Due: {{ formatAmount(calculateTotalDueAmount()) }}
          </span>
          <span
            v-else
            class="text-muted"
          >-</span>
        </span>
      </td>

      <!-- Payment Mode -->
      <td>
        <div v-if="receivedAmount.isEditing">
          <b-form-select
            v-model="receivedAmount.editData.payment_mode"
            :options="paymentModeOptions"
            :disabled="receivedAmount.isUpdating"
            size="sm"
            value-field="value"
            text-field="text"
            @change="handlePaymentModeChange(receivedAmount)"
          >
            <template #first>
              <b-form-select-option
                :value="null"
                disabled
              >
                Payment Mode
              </b-form-select-option>
            </template>
          </b-form-select>

          <!-- Borrower (only show when payment mode is loan) -->
          <b-form-select
            v-if="receivedAmount.editData.payment_mode === 'loan'"
            v-model="receivedAmount.editData.borrower"
            :options="borrowerOptions"
            :disabled="receivedAmount.isUpdating"
            size="sm"
            value-field="value"
            text-field="text"
            class="mt-50"
            :class="{ 'border-danger': !receivedAmount.editData.borrower }"
            @change="handleBorrowerChange(receivedAmount)"
          >
            <template #first>
              <b-form-select-option
                :value="null"
                disabled
              >
                Select Borrower
              </b-form-select-option>
            </template>
          </b-form-select>
          <!-- MR (only show when payment mode is mr_handover) -->
          <b-form-select
            v-else-if="receivedAmount.editData.payment_mode === 'mr_handover'"
            v-model="receivedAmount.editData.mr"
            :options="mrOptions"
            :disabled="receivedAmount.isUpdating"
            size="sm"
            value-field="value"
            text-field="text"
            class="mt-50"
            :class="{ 'border-danger': !receivedAmount.editData.mr }"
            @change="handleMrChange(receivedAmount)"
          >
            <template #first>
              <b-form-select-option
                :value="null"
                disabled
              >
                Select MR
              </b-form-select-option>
            </template>
          </b-form-select>
        </div>

        <b-badge
          v-else-if="receivedAmount.payment_mode"
          :variant="getPaymentModeVariant(receivedAmount.payment_mode)"
          class="text-capitalize"
        >
          {{ getPaymentModeName(receivedAmount.payment_mode) }}
        </b-badge>
        <span
          v-else
          class="text-muted"
        >-</span>
      </td>

      <!-- Remarks -->
      <td>
        <b-form-input
          v-if="receivedAmount.isEditing"
          v-model="receivedAmount.editData.remarks"
          size="sm"
          placeholder="Remarks"
          :disabled="receivedAmount.isUpdating || receivedAmount.editData.payment_mode === 'loan' || receivedAmount.editData.payment_mode === 'mr_handover'"
        />
        <feather-icon
          v-else-if="receivedAmount.remarks"
          v-b-tooltip.hover="{boundary:'window'}"
          :title="receivedAmount.remarks"
          icon="MessageSquareIcon"
          size="20"
          class="cursor-pointer"
        />
        <span
          v-else
          class="text-muted"
        >-</span>
      </td>

      <td>
        <div style="font-size: 0.75rem;">
          <div>{{ receivedAmount.created_at ? formatedDate(receivedAmount.created_at, 'DD-MM-YYYY LT') : '-' }}</div>
          <div class="text-primary">
            {{ receivedAmount.created_by_name || '-' }}
          </div>
        </div>
      </td>

      <!-- Actions -->
      <td>
        <template v-if="receivedAmount.isEditing">
          <b-spinner
            v-if="receivedAmount.isUpdating"
            small
            variant="success"
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="CheckIcon"
            size="18"
            class="mr-50 cursor-pointer text-success"
            :class="{ 'text-muted': !isValidEdit(receivedAmount) }"
            @click="isValidEdit(receivedAmount) && handleUpdate(receivedAmount, raIndex)"
          />
          <feather-icon
            icon="XIcon"
            size="18"
            class="cursor-pointer"
            :class="{ 'text-muted': receivedAmount.isUpdating }"
            @click="!receivedAmount.isUpdating && handleCancelEdit(receivedAmount, raIndex)"
          />
        </template>
        <template v-else>
          <!-- Show Edit/Delete if has ID -->
          <template v-if="receivedAmount.id">
            <feather-icon
              icon="EditIcon"
              size="16"
              class="mr-75 cursor-pointer text-info"
              :class="{ 'icon-disabled': receivedAmount.isDeleting }"
              @click="!receivedAmount.isDeleting && handleEdit(receivedAmount, raIndex)"
            />
            <b-spinner
              v-if="receivedAmount.isDeleting"
              small
              variant="danger"
              class="mr-25"
            />
            <feather-icon
              v-else
              icon="TrashIcon"
              size="16"
              class="cursor-pointer text-danger mr-75"
              @click="handleDelete(receivedAmount, raIndex)"
            />
          </template>
          <!-- Show Add icon for new rows or Plus icon to add more rows (only on last row) -->
          <template v-if="raIndex === billReceivedAmounts.length - 1">
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="cursor-pointer text-primary"
              @click="handleAddRow"
            />
          </template>
        </template>
      </td>
    </tr>
  </tbody>
</template>

<script>
import {
  BFormInput,
  BSpinner,
  BBadge,
  BFormCheckbox,
  BFormSelect,
  BFormSelectOption,
  VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'

export default {
  components: {
    BFormInput,
    BSpinner,
    BBadge,
    BFormCheckbox,
    BFormSelect,
    BFormSelectOption,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    bill: {
      type: Object,
      required: true,
    },
    billIndex: {
      type: Number,
      required: true,
    },
    paymentModeOptions: {
      type: Array,
      required: true,
    },
    borrowerOptions: {
      type: Array,
      required: true,
    },
    mrOptions: {
      type: Array,
      required: true,
    },
    paymentStatusOptions: {
      type: Array,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      billReceivedAmounts: [],
    }
  },
  mounted() {
    this.initializeReceivedAmounts()
  },
  methods: {
    initializeReceivedAmounts() {
      // If bill has received amounts, use them; otherwise create one empty row
      if (this.bill.received_amounts && this.bill.received_amounts.length > 0) {
        this.billReceivedAmounts = this.bill.received_amounts.map(ra => ({
          ...ra,
          isEditing: false,
          isUpdating: false,
          isDeleting: false,
          editData: {
            id: ra.id,
            received_amount: ra.received_amount,
            payment_mode: ra.payment_mode,
            borrower: ra.borrower || null,
            mr: ra.mr || null,
            remarks: ra.remarks || '',
          },
        }))
      } else {
        // Create one empty row for bills without received amounts
        this.billReceivedAmounts = [
          {
            id: null,
            received_amount: null,
            payment_mode: null,
            borrower: null,
            mr: null,
            remarks: '',
            created_at: null,
            created_by_name: null,
            isEditing: true, // Start in edit mode for new rows
            isUpdating: false,
            isDeleting: false,
            editData: {
              id: null,
              received_amount: null,
              payment_mode: null,
              borrower: null,
              mr: null,
              remarks: '',
            },
          },
        ]
      }
    },

    handleAddRow() {
      // Add a new empty row in edit mode
      this.billReceivedAmounts.push({
        id: null,
        received_amount: null,
        payment_mode: null,
        borrower: null,
        mr: null,
        remarks: '',
        created_at: null,
        created_by_name: null,
        isEditing: true,
        isUpdating: false,
        isDeleting: false,
        editData: {
          id: null,
          received_amount: null,
          payment_mode: null,
          borrower: null,
          mr: null,
          remarks: '',
        },
      })
    },

    handleEdit(receivedAmount, index) {
      // Use $set to ensure Vue reactivity
      this.$set(this.billReceivedAmounts[index], 'isEditing', true)
      this.$set(this.billReceivedAmounts[index], 'editData', {
        id: receivedAmount.id,
        received_amount: receivedAmount.received_amount,
        payment_mode: receivedAmount.payment_mode,
        borrower: receivedAmount.borrower || null,
        mr: receivedAmount.mr || null,
        remarks: receivedAmount.remarks || '',
      })
    },

    handleCancelEdit(receivedAmount, index) {
      // If it's a new row without ID, remove it
      if (!receivedAmount.id) {
        this.billReceivedAmounts.splice(index, 1)
        // Ensure at least one row exists
        if (this.billReceivedAmounts.length === 0) {
          this.handleAddRow()
        }
      } else {
        this.$set(this.billReceivedAmounts[index], 'isEditing', false)
        this.$set(this.billReceivedAmounts[index], 'editData', {
          id: null,
          received_amount: null,
          payment_mode: null,
          borrower: null,
          mr: null,
          remarks: '',
        })
      }
    },

    handlePaymentModeChange(receivedAmount) {
      if (receivedAmount.editData.payment_mode !== 'loan') {
        this.$set(receivedAmount.editData, 'borrower', null)
      }
      if (receivedAmount.editData.payment_mode !== 'mr_handover') {
        this.$set(receivedAmount.editData, 'mr', null)
      }
      // Clear remarks for loan or mr_handover (will be auto-filled)
      if (receivedAmount.editData.payment_mode === 'loan' || receivedAmount.editData.payment_mode === 'mr_handover') {
        this.$set(receivedAmount.editData, 'remarks', '')
      }
    },

    handleBorrowerChange(receivedAmount) {
      // Auto-fill remarks when borrower is selected for loan payment mode
      if (receivedAmount.editData.payment_mode === 'loan' && receivedAmount.editData.borrower) {
        const borrower = this.borrowerOptions.find(b => b.value === receivedAmount.editData.borrower)
        if (borrower) {
          this.$set(receivedAmount.editData, 'remarks', borrower.text)
        }
      }
    },

    handleMrChange(receivedAmount) {
      if (receivedAmount.editData.payment_mode === 'mr_handover' && receivedAmount.editData.mr) {
        const mr = this.mrOptions.find(m => m.value === receivedAmount.editData.mr)
        if (mr) {
          this.$set(receivedAmount.editData, 'remarks', mr.text)
        }
      }
    },

    async handleUpdate(receivedAmount, index) {
      if (!this.isValidEdit(receivedAmount)) return

      this.$set(this.billReceivedAmounts[index], 'isUpdating', true)

      try {
        const formData = {
          received_amount: receivedAmount.editData.received_amount,
          payment_mode: receivedAmount.editData.payment_mode,
          payment_status: 'paid', // Always send 'paid'
          remarks: receivedAmount.editData.remarks,
          invoice: this.bill.id, // Add invoice ID
        }

        // Add borrower if payment mode is loan
        if (receivedAmount.editData.payment_mode === 'loan' && receivedAmount.editData.borrower) {
          formData.borrower = receivedAmount.editData.borrower
        }
        // Add mr if payment mode is mr_handover
        if (receivedAmount.editData.payment_mode === 'mr_handover' && receivedAmount.editData.mr) {
          formData.mr = receivedAmount.editData.mr
        }

        let response
        if (receivedAmount.id) {
          // Update existing
          response = await axios.patch(`/operations/received_amount/${receivedAmount.id}/`, formData)
        } else {
          // Create new
          response = await axios.post('/operations/received_amount/', formData)
        }

        // If payment mode is loan and this is a new entry, create a loan record
        if (!receivedAmount.id && receivedAmount.editData.payment_mode === 'loan' && receivedAmount.editData.borrower) {
          await this.createLoanRecord(receivedAmount.editData)
        }

        if (!receivedAmount.id && receivedAmount.editData.payment_mode === 'mr_handover' && receivedAmount.editData.mr) {
          await this.createMrLoanRecord(receivedAmount.editData)
        }

        // Update the row with response data using $set
        this.$set(this.billReceivedAmounts, index, {
          ...response.data,
          isEditing: false,
          isUpdating: false,
          isDeleting: false,
          editData: {
            id: null,
            received_amount: null,
            payment_mode: null,
            borrower: null,
            mr: null,
            remarks: '',
          },
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: receivedAmount.id ? 'Received amount updated successfully' : 'Received amount added successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Emit to parent to refresh data
        this.$emit('data-updated')
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.response?.data?.message || 'Failed to save received amount',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.$set(this.billReceivedAmounts[index], 'isUpdating', false)
      }
    },
    async createMrLoanRecord(editData) {
      try {
        const mrLoanFormData = {
          mr: editData.mr,
          amount: editData.received_amount,
          remarks: this.bill.bill_no,
          status: 'open',
        }

        await axios.post('/operations/mr_loans/', mrLoanFormData)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'MR loan record created successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            text: error.response?.data?.message || 'Received amount saved but failed to create MR loan record',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      }
    },
    async createLoanRecord(editData) {
      try {
        const loanFormData = {
          borrower: editData.borrower,
          amount: editData.received_amount,
          remarks: this.bill.bill_no,
          status: 'open',
        }

        await axios.post('/operations/loans/', loanFormData)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Loan record created successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            text: error.response?.data?.message || 'Received amount saved but failed to create loan record',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      }
    },

    handleDelete(receivedAmount, index) {
      this.$bvModal
        .msgBoxConfirm('You won\'t be able to revert this!', {
          title: 'Are you sure?',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes, delete it!',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(async value => {
          if (value) {
            await this.confirmDelete(receivedAmount, index)
          }
        })
    },

    handleReceivedAmountFocus(receivedAmount, event) {
      const { editData } = receivedAmount

      // Only auto-fill if the field is empty or null
      if (!editData.received_amount) {
        const dueAmount = this.calculateTotalDueAmount()

        // Only fill if there's a positive due amount
        if (dueAmount > 0) {
          this.$set(editData, 'received_amount', dueAmount)

          // For number inputs, blur and focus to move cursor to end
          this.$nextTick(() => {
            if (event && event.target) {
              const input = event.target
              input.blur()
              // Use setTimeout to ensure the blur completes
              setTimeout(() => {
                input.focus()
              }, 0)
            }
          })
        }
      }
    },

    async confirmDelete(receivedAmount, index) {
      this.$set(this.billReceivedAmounts[index], 'isDeleting', true)

      try {
        await axios.delete(`/operations/received_amount/${receivedAmount.id}/`)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Received amount deleted successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Remove the row
        this.billReceivedAmounts.splice(index, 1)

        // Ensure at least one row exists
        if (this.billReceivedAmounts.length === 0) {
          this.handleAddRow()
        }

        // Emit to parent to refresh data
        this.$emit('data-updated')
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.response?.data?.message || 'Failed to delete received amount',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.$set(this.billReceivedAmounts[index], 'isDeleting', false)
      }
    },

    isValidEdit(receivedAmount) {
      const isAmountValid = receivedAmount.editData.received_amount && receivedAmount.editData.received_amount > 0
      const isPaymentModeValid = receivedAmount.editData.payment_mode

      if (receivedAmount.editData.payment_mode === 'loan') {
        return isAmountValid && isPaymentModeValid && receivedAmount.editData.borrower
      }

      if (receivedAmount.editData.payment_mode === 'mr_handover') {
        return isAmountValid && isPaymentModeValid && receivedAmount.editData.mr
      }

      return isAmountValid && isPaymentModeValid
    },

    getPartyName(identifier) {
      if (!identifier) return '-'
      const parts = identifier.split('|')
      return parts[0]?.trim() || '-'
    },

    getPartyArea(identifier) {
      if (!identifier) return '-'
      const parts = identifier.split('|')
      return parts[1]?.trim() || '-'
    },

    calculateTotalDueAmount() {
      // Calculate total received amounts
      const totalReceived = this.billReceivedAmounts.reduce((sum, ra) => sum + (ra.received_amount || 0), 0)

      // Due amount = Bill amount - Total received
      return (this.bill.amount || 0) - totalReceived
    },

    getDueAmountClass() {
      const dueAmount = this.calculateTotalDueAmount()
      if (dueAmount > 0) return 'text-danger font-weight-bold'
      if (dueAmount === 0) return 'text-success font-weight-bold'
      return 'text-warning font-weight-bold' // Overpaid
    },

    getPaymentModeName(mode) {
      const paymentMode = this.paymentModeOptions.find(m => m.value === mode)
      return paymentMode ? paymentMode.text : mode
    },

    getPaymentModeVariant(mode) {
      const variants = {
        cash: 'success',
        upi: 'primary',
        cheque: 'info',
        rgts: 'warning',
        credit_note: 'secondary',
        challan_converted_bill: 'dark',
        loan: 'danger',
        mr_handover: 'info',
      }
      return variants[mode] || 'secondary'
    },

    getPayStatusVariant(status) {
      const statusLower = status.toLowerCase()
      const variants = {
        paid: 'success',
        unpaid: 'danger',
        'partially paid': 'warning',
        partially_paid: 'warning',
        partial: 'warning',
      }
      return variants[statusLower] || 'secondary'
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    getRowClass() {
      const baseClass = this.isSelected ? 'selected-row' : ''

      // No background if no received amounts
      if (!this.bill.received_amounts || this.bill.received_amounts.length === 0) {
        return baseClass
      }

      // Calculate due amount
      const dueAmount = this.calculateTotalDueAmount()

      // Red background if due amount > 0
      if (dueAmount > 0) {
        return `${baseClass} table-danger`
      }

      // Use calc_payment_status for other cases
      if (!this.bill.calc_payment_status) return baseClass

      const statusLower = this.bill.calc_payment_status.toLowerCase()
      if (statusLower === 'paid') {
        return `${baseClass} table-success`
      }
      if (statusLower === 'partially_paid' || statusLower === 'partial') {
        return `${baseClass} table-warning`
      }

      return baseClass
    },

    // Status dot methods
    getStatusDotClass(dotIndex) {
      const classes = {
        1: 'status-dot-gray',
        2: 'status-dot-primary',
        3: 'status-dot-yellow',
        4: 'status-dot-orange',
        5: 'status-dot-green',
      }
      return classes[dotIndex] || 'status-dot-gray'
    },

    getStatusTooltip(dotIndex) {
      const tooltips = {
        1: 'Created',
        2: 'Confirmed',
        3: 'Packed',
        4: 'Picked',
        5: 'Delivered',
      }
      return tooltips[dotIndex] || 'Unknown Status'
    },

    // Selection methods
    handleSelectionChange(isChecked) {
      this.$emit('selection-changed', {
        billId: this.bill.id,
        isSelected: isChecked,
      })
    },

    handleRowClick(event) {
      // Prevent toggle on input/button/select clicks
      if (
        event.target.tagName === 'INPUT'
        || event.target.tagName === 'BUTTON'
        || event.target.tagName === 'SELECT'
        || event.target.closest('.cursor-pointer')
      ) {
        return
      }

      // Toggle selection
      this.$emit('selection-changed', {
        billId: this.bill.id,
        isSelected: !this.isSelected,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
/* Border danger for validation */
.border-danger {
  border-color: #ea5455 !important;
}

/* Icon disabled styling */
.icon-disabled {
  pointer-events: none;
  opacity: 0.3;
  cursor: not-allowed;
}

/* Table row color classes */
.table-success {
  background-color: #d4edda !important;
}

.table-danger {
  background-color: #f8d7da !important;
}

.table-warning {
  background-color: #fff3cd !important;
}

/* Selected row styling */
.selected-row {
  background-color: #e3f2fd !important;

  &.table-success {
    background-color: #c8e6c9 !important;
  }

  &.table-danger {
    background-color: #ffcdd2 !important;
  }

  &.table-warning {
    background-color: #ffe082 !important;
  }
}

/* Cursor pointer for clickable icons */
.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

/* Badge styling */
.badge {
  padding: 0.35rem 0.5rem;
  font-size: 0.75rem;
}

/* Small form controls */
.form-control-sm {
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.mr-25 {
  margin-right: 0.25rem !important;
}

.mr-50 {
  margin-right: 0.5rem !important;
}

.mr-75 {
  margin-right: 0.75rem !important;
}

.mt-25 {
  margin-top: 0.25rem !important;
}

.mt-50 {
  margin-top: 0.5rem !important;
}

.text-muted {
  opacity: 0.5;
}

/* Status dot styles */
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  cursor: help;
}

.status-dot-gray {
  background-color: #6c757d;
}

.status-dot-primary {
  background-color: #007bff;
}

.status-dot-yellow {
  background-color: #ffc107;
}

.status-dot-orange {
  background-color: #fd7e14;
}

.status-dot-green {
  background-color: #28a745;
}

/* Flexbox utility */
.d-flex {
  display: flex !important;
}

.align-items-center {
  align-items: center !important;
}

/* Row clickable cursor */
tbody tr {
  cursor: pointer;
}
</style>
